<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import VIcon from "../Icon/VIcon.vue";
import VTooltip from "../Tooltip/VTooltip.vue";

defineOptions({
  name: "VHelpTooltip",
});

/**
 * The question mark right after a field label or a title that shows its help text. Help never sits as a line of
 * text under a field or title: text that only repeats the label is dropped, the rest lives here. The button is
 * focusable and the tooltip opens on hover, focus and touch, so keyboard, screen reader and touch users reach the
 * same text as a mouse.
 */
const { content, subject = "", label: ownLabel = "" } = defineProps<{
  /** The help text. */
  content: string
  /** The label or title the help belongs to; names the button ("Meer informatie over Naam"). */
  subject?: string
  /** The button's own name, for a place where "Meer informatie over …" does not read well. Wins over `subject`. */
  label?: string
}>();

const { t } = useI18n();

const label = computed(() => {
  if (ownLabel) {
    return ownLabel;
  }

  return subject ? t("base.help_about", { subject }) : t("base.help");
});
</script>

<template>
  <VTooltip placement="top" class="inline-flex shrink-0 align-middle">
    <template #reference>
      <button
        type="button"
        class="inline-flex size-5 shrink-0 cursor-help items-center justify-center rounded-full text-gray-600 hover:text-(--text) focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-400 dark:hover:text-(--text)"
        :aria-label="label"
        data-testid="help-tooltip"
        @click.stop.prevent
      >
        <VIcon name="circle-question-mark" class="text-sm" aria-hidden="true" />
      </button>
    </template>
    <template #content>
      <div class="max-w-64 whitespace-normal" data-testid="help-tooltip-content">
        {{ content }}
      </div>
    </template>
  </VTooltip>
</template>
